import {
  isFacebookBrowser,
  isIOSSafari,
  isRunningOnClient,
  isSamsungBrowser,
} from "@/utils/browser";
import { notifyError } from "@/utils/monitoring";
import { trackLegacyEvent } from "@analytics";
import type React from "react";
import {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: Array<string>;
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export type CustomPromptModalType =
  | "ios"
  | "samsung"
  | "facebook"
  | "other"
  | null;
export type PromptInstallOutcome =
  | "accepted"
  | "dismissed"
  | "custom_instructions_triggered"
  | "error"
  | "not_applicable";

interface PWAInstallContextType {
  installPromptEvent: BeforeInstallPromptEvent | null;
  setInstallPromptEvent: (event: BeforeInstallPromptEvent | null) => void;
  isPWAInstalled: boolean;
  promptInstall: () => Promise<PromptInstallOutcome>;
  isCustomInstructionsModalOpen: boolean;
  customPromptModalType: CustomPromptModalType;
  closeCustomInstructionsModal: () => void;
}

const PWAInstallContext = createContext<PWAInstallContextType | undefined>(
  undefined,
);

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

export const PWAInstallProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [installPromptEvent, setInstallPromptEvent] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isPWAInstalled, setIsPWAInstalled] = useState(false);
  const [isCustomInstructionsModalOpen, setIsCustomInstructionsModalOpen] =
    useState(false);
  const [customPromptModalType, setCustomPromptModalType] =
    useState<CustomPromptModalType>(null);

  useEffect(() => {
    if (!isRunningOnClient()) return;

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPromptEvent(event as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setIsPWAInstalled(true);
      // Optionally clear the prompt event if the app is installed via other means
      setInstallPromptEvent(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    // Check if the PWA is already installed (e.g., for standalone mode)
    if (
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as NavigatorWithStandalone).standalone === true // Use defined type
    ) {
      setIsPWAInstalled(true);
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const promptInstall = async (): Promise<PromptInstallOutcome> => {
    if (installPromptEvent) {
      try {
        trackLegacyEvent(
          {
            screen: "PWAInstallPrompt",
            userJourney: "PWAInstallation",
            action: "Opened",
            extras: {
              platform: installPromptEvent.platforms[0],
            },
          },
          "Play",
        );
        installPromptEvent.prompt();
        const { outcome } = await installPromptEvent.userChoice;
        trackLegacyEvent(
          {
            screen: "PWAInstallPrompt",
            userJourney: "PWAInstallation",
            action: "Pressed",
            extras: {
              platform: installPromptEvent.platforms[0],
              outcome,
            },
          },
          "Play",
        );
        if (outcome === "accepted") {
          setInstallPromptEvent(null); // Clear event after successful install
          setIsPWAInstalled(true);
          return "accepted";
        }
        // Don't clear the event if dismissed, allow retry
        trackLegacyEvent(
          {
            screen: "PWAInstallPrompt",
            userJourney: "PWAInstallation",
            action: "Dismissed",
            extras: {
              platform: installPromptEvent.platforms[0],
              outcome,
            },
          },
          "Play",
        );
        return "dismissed";
      } catch (error) {
        notifyError(error, "discoverFeedError");
        setInstallPromptEvent(null); // Clear on error to prevent repeated failures with the same event
        setIsCustomInstructionsModalOpen(true);
        return "error";
      }
    } else if (isIOSSafari()) {
      setCustomPromptModalType("ios");
      setIsCustomInstructionsModalOpen(true);
      return "custom_instructions_triggered";
    } else if (isSamsungBrowser()) {
      setCustomPromptModalType("samsung");
      setIsCustomInstructionsModalOpen(true);
      return "custom_instructions_triggered";
    } else if (isFacebookBrowser()) {
      setCustomPromptModalType("facebook");
      setIsCustomInstructionsModalOpen(true);
      return "custom_instructions_triggered";
    } else if (isRunningOnClient()) {
      setCustomPromptModalType("other");
      setIsCustomInstructionsModalOpen(true);
      return "custom_instructions_triggered";
    }
    return "not_applicable"; // e.g., Desktop browser with no specific instructions or already installed
  };

  const closeCustomInstructionsModal = () => {
    setIsCustomInstructionsModalOpen(false);
    setCustomPromptModalType(null);
  };

  return (
    <PWAInstallContext.Provider
      value={{
        installPromptEvent,
        setInstallPromptEvent,
        isPWAInstalled,
        promptInstall,
        isCustomInstructionsModalOpen,
        customPromptModalType,
        closeCustomInstructionsModal,
      }}
    >
      {children}
    </PWAInstallContext.Provider>
  );
};

export const usePWAInstall = () => {
  const context = useContext(PWAInstallContext);
  if (context === undefined) {
    throw new Error("usePWAInstall must be used within a PWAInstallProvider");
  }
  return context;
};
